import Service, { service } from '@ember/service';
import { CAPABILITY_SOURCE_RANK, type CapabilitySource } from 'tio-common/services/features';
import type FeaturesService from 'tio-common/services/features';
import type PartnerService from 'tio-employee/services/partner';
import type { PhoneCaptureSurface } from 'tio-data/data/resources/company-setting.type';

/** Every capability this source answers. Anything else falls through untouched. */
const KEY_PREFIX = 'phoneCapture';

/**
 * Resolves the RFC 110 phone-capture capabilities from two independent gates, both of
 * which must be on before any surface appears:
 *
 *   environment — `ENV.featureFlags.phoneCapture`, false in the production build, so a
 *                 production bundle hides all five surfaces no matter what a company
 *                 has configured. Flipping it is the go-live switch.
 *   company     — the API's already-resolved `phoneCapture` payload, per partner.
 *
 * Registered as a capability source rather than read directly by templates so a surface
 * asks one question (`features.can('phoneCapture.surfaces.profile')`) instead of
 * repeating both gates and getting one of them wrong.
 *
 * Keys: `phoneCapture` (master), `phoneCapture.surfaces.<surface>`, and
 * `phoneCapture.postLoginOverlay.mobileOnly`.
 */
export default class PhoneCaptureService extends Service {
  @service declare features: FeaturesService;
  @service declare partner: PartnerService;

  /** The environment half. False in production until go-live. */
  get isAvailable(): boolean {
    try {
      return this.features.isEnabled(KEY_PREFIX);
    } catch {
      // isEnabled throws when ENV is unseeded; capabilities gate boot-time templates.
      return false;
    }
  }

  /** Whether the partner has switched the feature on at all. */
  get isEnabledForCompany(): boolean {
    return this.isAvailable && this.partner.phoneCapture?.isEnabled === true;
  }

  /**
   * @param surface one of the five RFC 110 surfaces
   * @return false unless the environment flag, the company switch, and that surface
   *   are all on
   */
  isSurfaceEnabled(surface: PhoneCaptureSurface): boolean {
    return this.isEnabledForCompany && this.partner.phoneCapture?.surfaces?.[surface] === true;
  }

  /** Rollout lever: restrict the post-login overlay to mobile viewports. */
  get overlayIsMobileOnly(): boolean {
    return (
      this.isEnabledForCompany && this.partner.phoneCapture?.postLoginOverlay?.mobileOnly === true
    );
  }

  /**
   * Abstains (`undefined`) on every key it does not own, so unrelated capabilities keep
   * falling through to the flag and default tiers.
   */
  asCapabilitySource(): CapabilitySource {
    return {
      id: 'phone-capture',
      rank: CAPABILITY_SOURCE_RANK.entitlement,
      resolve: (key) => {
        const name = key as string;

        if (name !== KEY_PREFIX && !name.startsWith(`${KEY_PREFIX}.`)) {
          return undefined;
        }

        if (name === KEY_PREFIX) {
          return this.isEnabledForCompany;
        }

        if (name === `${KEY_PREFIX}.postLoginOverlay.mobileOnly`) {
          return this.overlayIsMobileOnly;
        }

        const surface = name.startsWith(`${KEY_PREFIX}.surfaces.`)
          ? (name.slice(`${KEY_PREFIX}.surfaces.`.length) as PhoneCaptureSurface)
          : null;

        // An unknown phoneCapture.* key is answered false rather than abstaining: a
        // typo must not fall through to the flag tier and read as enabled.
        return surface ? this.isSurfaceEnabled(surface) : false;
      },
    };
  }
}
